import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import ConfirmDialog from './ConfirmDialog';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ListInput from './ListInput';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import {
  Route,
  Globe,
  Smartphone,
  Monitor,
  Clock,
  Plus,
  Edit,
  Trash2,
  Settings,
  Target,
  BarChart3,
  ExternalLink,
  Copy,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';

export default function TrafficManagement() {
  const { user } = useAuth();
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  const [deleteTarget, setDeleteTarget] = useState(null);

  // State for campaigns, rules, pages, offers
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [trafficRules, setTrafficRules] = useState([]);
  const [landingPages, setLandingPages] = useState([]);
  const [offers, setOffers] = useState([]);

  // Dialog states
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [pageDialogOpen, setPageDialogOpen] = useState(false);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    countries: [],
    cities: [],
    devices: [],
    browsers: [],
    os_list: [],
    referrer_include: [],
    referrer_exclude: [],
    keyword_include: [],
    keyword_exclude: [],
    redirect_url: '',
    weight: 100,
    priority: 1
  });

  const [pageForm, setPageForm] = useState({
    name: '',
    url: '',
    description: '',
    category: '',
    tags: []
  });

  const [offerForm, setOfferForm] = useState({
    name: '',
    url: '',
    description: '',
    payout: 0,
    currency: 'USD',
    category: '',
    network: '',
    countries: []
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      fetchTrafficRules();
    }
  }, [selectedCampaign]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      const [campaignsRes, pagesRes, offersRes] = await Promise.allSettled([
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/landing-pages`),
        axios.get(`${API}/offers`),
      ]);
      const campaignsData = campaignsRes.status === 'fulfilled' ? campaignsRes.value.data : [];
      if (campaignsRes.status === 'fulfilled') setCampaigns(campaignsData);
      if (pagesRes.status === 'fulfilled') setLandingPages(pagesRes.value.data);
      if (offersRes.status === 'fulfilled') setOffers(offersRes.value.data);

      if (campaignsData.length > 0) {
        setSelectedCampaign(campaignsData[0].id);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTrafficRules = async () => {
    if (!selectedCampaign) return;

    try {
      const response = await axios.get(`${API}/traffic-rules/campaign/${selectedCampaign}`);
      setTrafficRules(response.data);
    } catch (error) {
      console.error('Error fetching traffic rules:', error);
      toast.error(t('Failed to load traffic rules'));
    }
  };

  const handleCreateRule = async () => {
    if (!selectedCampaign) {
      toast.error(t('Select a campaign'));
      return;
    }

    try {
      const response = await axios.post(`${API}/traffic-rules/campaign/${selectedCampaign}`, ruleForm);
      setTrafficRules([...trafficRules, response.data]);
      setRuleDialogOpen(false);
      resetRuleForm();
      toast.success(t('Traffic rule created'));
    } catch (error) {
      console.error('Error creating rule:', error);
      toast.error(t('Failed to create rule'));
    }
  };

  const handleUpdateRule = async () => {
    if (!editingItem) return;

    try {
      const response = await axios.put(`${API}/traffic-rules/${editingItem.id}`, ruleForm);
      setTrafficRules(trafficRules.map(rule => rule.id === editingItem.id ? response.data : rule));
      setRuleDialogOpen(false);
      setEditingItem(null);
      resetRuleForm();
      toast.success(t('Rule updated'));
    } catch (error) {
      console.error('Error updating rule:', error);
      toast.error(t('Failed to update rule'));
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/traffic-rules/${ruleId}`);
      setTrafficRules(trafficRules.filter(rule => rule.id !== ruleId));
      toast.success(t('Rule deleted'));
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error(t('Failed to delete rule'));
    }
  };

  const handleCreatePage = async () => {
    try {
      const response = await axios.post(`${API}/landing-pages`, pageForm);
      setLandingPages([...landingPages, response.data]);
      setPageDialogOpen(false);
      resetPageForm();
      toast.success(t('Landing created'));
    } catch (error) {
      console.error('Error creating page:', error);
      toast.error(t('Failed to create landing'));
    }
  };

  const handleCreateOffer = async () => {
    try {
      const response = await axios.post(`${API}/offers`, offerForm);
      setOffers([...offers, response.data]);
      setOfferDialogOpen(false);
      resetOfferForm();
      toast.success(t('Offer created'));
    } catch (error) {
      console.error('Error creating offer:', error);
      toast.error(t('Failed to create offer'));
    }
  };

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      countries: [],
      cities: [],
      devices: [],
      browsers: [],
      os_list: [],
      referrer_include: [],
      referrer_exclude: [],
      keyword_include: [],
      keyword_exclude: [],
      redirect_url: '',
      weight: 100,
      priority: 1
    });
  };

  const resetPageForm = () => {
    setPageForm({
      name: '',
      url: '',
      description: '',
      category: '',
      tags: []
    });
  };

  const resetOfferForm = () => {
    setOfferForm({
      name: '',
      url: '',
      description: '',
      payout: 0,
      currency: 'USD',
      category: '',
      network: '',
      countries: []
    });
  };

  const openEditRule = (rule) => {
    setEditingItem(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      countries: rule.countries || [],
      cities: rule.cities || [],
      devices: rule.devices || [],
      browsers: rule.browsers || [],
      os_list: rule.os_list || [],
      referrer_include: rule.referrer_include || [],
      referrer_exclude: rule.referrer_exclude || [],
      keyword_include: rule.keyword_include || [],
      keyword_exclude: rule.keyword_exclude || [],
      redirect_url: rule.redirect_url,
      weight: rule.weight,
      priority: rule.priority
    });
    setRuleDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Routing')}
        title={t('Fallback rules')}
        subtitle={t('Used only when a click matches none of the campaign streams. Set up routing in streams; keep these for rare catch-all cases.')}
        actions={
          campaigns.length > 0 ? (
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder={t('Select campaign')} />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap h-auto justify-start">
          <TabsTrigger value="rules" className="flex items-center space-x-2">
            <Filter className="w-4 h-4" />
            <span>{t('Fallback rules')}</span>
          </TabsTrigger>
          <TabsTrigger value="landings" className="flex items-center space-x-2">
            <Target className="w-4 h-4" />
            <span>{t('Landings')}</span>
          </TabsTrigger>
          <TabsTrigger value="offers" className="flex items-center space-x-2">
            <BarChart3 className="w-4 h-4" />
            <span>{t('Offers')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Traffic Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('Traffic distribution rules')}</h2>
            <Dialog open={ruleDialogOpen} onOpenChange={(open) => { setRuleDialogOpen(open); if (!open) { setEditingItem(null); resetRuleForm(); } }}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingItem(null); resetRuleForm(); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Create rule')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingItem ? t('Edit rule') : t('Create traffic rule')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('Configure conditions and actions for traffic distribution')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('Name')}</Label>
                      <Input
                        value={ruleForm.name}
                        onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('Rule name')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('Priority')}</Label>
                      <Input
                        type="number"
                        value={ruleForm.priority}
                        onChange={(e) => setRuleForm(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                        min="1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Description')}</Label>
                    <Textarea
                      value={ruleForm.description}
                      onChange={(e) => setRuleForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t('Rule description')}
                    />
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium">{t('Conditions (leave empty for any value)')}</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('Countries (ISO codes)')}</Label>
                        <ListInput
                          value={ruleForm.countries}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            countries: list
                          }))}
                          placeholder={t('US, CA, GB')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('Cities')}</Label>
                        <ListInput
                          value={ruleForm.cities}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            cities: list
                          }))}
                          placeholder={t('Moscow, New York')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('Devices')}</Label>
                        <ListInput
                          value={ruleForm.devices}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            devices: list
                          }))}
                          placeholder={t('Desktop, Mobile, Tablet')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('Browsers')}</Label>
                        <ListInput
                          value={ruleForm.browsers}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            browsers: list
                          }))}
                          placeholder="Chrome, Firefox, Safari"
                        />
                      </div>
                    </div>

                    <Separator className="my-3" />
                    <h4 className="font-medium">{t('Referrer / Keyword filters')}</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('Referrer include')}</Label>
                        <ListInput
                          value={ruleForm.referrer_include}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            referrer_include: list
                          }))}
                          placeholder="facebook.com, google.com"
                        />
                        <p className="text-xs text-muted-foreground">{t('Only match if referrer contains one of these')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('Referrer exclude')}</Label>
                        <ListInput
                          value={ruleForm.referrer_exclude}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            referrer_exclude: list
                          }))}
                          placeholder="bot.com, spam.net"
                        />
                        <p className="text-xs text-muted-foreground">{t('Skip if referrer contains one of these')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('Keyword include')}</Label>
                        <ListInput
                          value={ruleForm.keyword_include}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            keyword_include: list
                          }))}
                          placeholder={t('buy, cheap, discount')}
                        />
                        <p className="text-xs text-muted-foreground">{t('Match if referrer/params contain a keyword')}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('Keyword exclude')}</Label>
                        <ListInput
                          value={ruleForm.keyword_exclude}
                          onChange={(list) => setRuleForm(prev => ({
                            ...prev,
                            keyword_exclude: list
                          }))}
                          placeholder={t('free, demo')}
                        />
                        <p className="text-xs text-muted-foreground">{t('Skip if referrer/params contain a keyword')}</p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <h4 className="font-medium">{t('Action')}</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('Redirect URL')}</Label>
                        <Input
                          value={ruleForm.redirect_url}
                          onChange={(e) => setRuleForm(prev => ({ ...prev, redirect_url: e.target.value }))}
                          placeholder="https://example.com"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('Weight (%)')}</Label>
                        <Input
                          type="number"
                          value={ruleForm.weight}
                          onChange={(e) => setRuleForm(prev => ({ ...prev, weight: parseInt(e.target.value) || 0 }))}
                          min="0"
                          max="100"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setRuleDialogOpen(false)}>
                      {t('Cancel')}
                    </Button>
                    <Button onClick={editingItem ? handleUpdateRule : handleCreateRule}>
                      {editingItem ? t('Update') : t('Create')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {selectedCampaign ? (
            <div className="grid gap-4">
              {trafficRules.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-12">
                    <Filter className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">{t('No traffic rules')}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('Create rules to distribute traffic by conditions')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                trafficRules.map((rule) => (
                  <Card key={rule.id} className="border">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CardTitle className="text-lg">{rule.name}</CardTitle>
                          <Badge variant={rule.is_active ? "default" : "secondary"}>
                            {rule.is_active ? t('Active') : t('Paused')}
                          </Badge>
                          <Badge variant="outline">
                            {t('Priority')}: {rule.priority}
                          </Badge>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button variant="outline" size="sm" aria-label={t('Edit rule')} title={t('Edit rule')} onClick={() => openEditRule(rule)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="sm" aria-label={t('Delete rule')} title={t('Delete rule')} onClick={() => setDeleteTarget(rule)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {rule.description && (
                        <CardDescription>{rule.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('Countries')}</Label>
                          <p>{rule.countries.length > 0 ? rule.countries.join(', ') : t('Any')}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('Devices')}</Label>
                          <p>{rule.devices.length > 0 ? rule.devices.join(', ') : t('Any')}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('Weight')}</Label>
                          <p>{rule.weight}%</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">{t('Redirects')}</Label>
                          <p>{rule.total_redirects}</p>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-muted-foreground">{t('Redirect URL')}</Label>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('Open redirect URL')}
                            title={t('Open redirect URL')}
                            onClick={() => window.open(rule.redirect_url, '_blank', 'noopener,noreferrer')}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="text-sm font-mono bg-surface-2 p-2 rounded mt-1 truncate">
                          {rule.redirect_url}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Route className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{t('Select a campaign to manage traffic rules')}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Landing Pages Tab */}
        <TabsContent value="landings" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('Landings')}</h2>
            <Dialog open={pageDialogOpen} onOpenChange={(open) => { setPageDialogOpen(open); if (!open) { setEditingItem(null); resetPageForm(); } }}>
              <DialogTrigger asChild>
                <Button onClick={resetPageForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Add landing')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('Add landing')}</DialogTitle>
                  <DialogDescription>
                    {t('Add a new landing to the library')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('Name')}</Label>
                    <Input
                      value={pageForm.name}
                      onChange={(e) => setPageForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('Landing name')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>URL</Label>
                    <Input
                      value={pageForm.url}
                      onChange={(e) => setPageForm(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Category')}</Label>
                    <Select
                      value={pageForm.category}
                      onValueChange={(value) => setPageForm(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('Select category')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ecommerce">{t('E-commerce')}</SelectItem>
                        <SelectItem value="lead-gen">{t('Lead Generation')}</SelectItem>
                        <SelectItem value="app-install">{t('App Install')}</SelectItem>
                        <SelectItem value="content">{t('Content')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Description')}</Label>
                    <Textarea
                      value={pageForm.description}
                      onChange={(e) => setPageForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t('Landing description')}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setPageDialogOpen(false)}>
                      {t('Cancel')}
                    </Button>
                    <Button onClick={handleCreatePage}>
                      {t('Create')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {landingPages.map((page) => (
              <Card key={page.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{page.name}</CardTitle>
                    <Badge variant={page.is_active ? "default" : "secondary"}>
                      {page.is_active ? t('Active') : t('Paused')}
                    </Badge>
                  </div>
                  {page.category && (
                    <CardDescription>{page.category}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">URL</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('Open landing page')}
                        title={t('Open landing page')}
                        onClick={() => window.open(page.url, '_blank', 'noopener,noreferrer')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm font-mono bg-surface-2 p-2 rounded truncate">
                      {page.url}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center pt-3 border-t border-border">
                      <div>
                        <div className="text-lg font-semibold">{page.total_visits}</div>
                        <div className="text-xs text-muted-foreground">{t('Visits')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{page.conversions}</div>
                        <div className="text-xs text-muted-foreground">{t('Conversions')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{(Number(page.conversion_rate) || 0).toFixed(1)}%</div>
                        <div className="text-xs text-muted-foreground">CR</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {landingPages.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="text-center py-12">
                  <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('No landings')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Add landings to use in traffic rules')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('Offers')}</h2>
            <Dialog open={offerDialogOpen} onOpenChange={(open) => { setOfferDialogOpen(open); if (!open) { setEditingItem(null); resetOfferForm(); } }}>
              <DialogTrigger asChild>
                <Button onClick={resetOfferForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Add offer')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('Add offer')}</DialogTitle>
                  <DialogDescription>
                    {t('Add a new offer to monetize traffic')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('Name')}</Label>
                      <Input
                        value={offerForm.name}
                        onChange={(e) => setOfferForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder={t('Offer name')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('Network')}</Label>
                      <Input
                        value={offerForm.network}
                        onChange={(e) => setOfferForm(prev => ({ ...prev, network: e.target.value }))}
                        placeholder={t('Affiliate network')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Offer URL')}</Label>
                    <Input
                      value={offerForm.url}
                      onChange={(e) => setOfferForm(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('Payout')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={offerForm.payout}
                        onChange={(e) => setOfferForm(prev => ({ ...prev, payout: parseFloat(e.target.value) || 0 }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('Currency')}</Label>
                      <Select
                        value={offerForm.currency}
                        onValueChange={(value) => setOfferForm(prev => ({ ...prev, currency: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="RUB">RUB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('Description')}</Label>
                    <Textarea
                      value={offerForm.description}
                      onChange={(e) => setOfferForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t('Offer description')}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setOfferDialogOpen(false)}>
                      {t('Cancel')}
                    </Button>
                    <Button onClick={handleCreateOffer}>
                      {t('Create')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offers.map((offer) => (
              <Card key={offer.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{offer.name}</CardTitle>
                    <Badge variant={offer.is_active ? "default" : "secondary"}>
                      {offer.is_active ? t('Active') : t('Paused')}
                    </Badge>
                  </div>
                  {offer.network && (
                    <CardDescription>{offer.network}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold">
                        {offer.payout} {offer.currency}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('Open offer URL')}
                        title={t('Open offer URL')}
                        onClick={() => window.open(offer.url, '_blank', 'noopener,noreferrer')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center pt-3 border-t border-border">
                      <div>
                        <div className="text-lg font-semibold">{offer.total_clicks}</div>
                        <div className="text-xs text-muted-foreground">{t('Clicks')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{offer.conversions}</div>
                        <div className="text-xs text-muted-foreground">{t('Conversions')}</div>
                      </div>
                      <div>
                        <div className="text-lg font-semibold">{(Number(offer.epc) || 0).toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">EPC</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {offers.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="text-center py-12">
                  <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t('No offers')}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Add offers to monetize traffic')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete traffic rule')}
        description={t('This permanently deletes the traffic rule. Traffic will fall through to the remaining rules or the campaign default.')}
        confirmLabel={t('Delete')}
        onConfirm={() => { const id = deleteTarget && deleteTarget.id; setDeleteTarget(null); if (id) handleDeleteRule(id); }}
      />
    </div>
  );
}
